'use client';

import { c as _c } from "react/compiler-runtime";
import { HeartIcon as HeartIconOutline } from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import { motion, useAnimationControls } from 'motion/react';
import { useCallback, useEffect, useState } from 'react';
import { favoriteEntity, unfavoriteEntity } from '@src/actions/user.actions';
import { useFavoriteState } from '@src/hooks/useFavoriteState';
import { log } from '@src/utils/log.util';
import { cn } from '@src/utils/tailwind';
import { instrumentAction } from "@src/utils/instrumentation";
type FavoriteButtonProps = {
  entityId?: string;
  entityType?: CocoApi.EntityType | CocoApi.ContentType;
  isFavorited?: boolean;
  size?: 'large' | 'small';
  tone?: 'dark' | 'light';
  removeOutline?: boolean;
  className?: string;
  onFavoriteClick?: (isFavorited: boolean) => void;
};
function AnimatedFavoriteIcon(t0) {
  const $ = _c(32);
  const {
    isFavorited,
    animationKey,
    className
  } = t0;
  const controls = useAnimationControls();
  let t1;
  let t2;
  if ($[0] !== animationKey || $[1] !== controls) {
    t1 = () => {
      if (animationKey === 0) {
        return;
      }
      controls.start({
        scale: [1, 0.85, 1.15, 1],
        transition: {
          duration: 0.35,
          times: [0, 0.2, 0.65, 1],
          ease: "easeOut"
        }
      });
    };
    t2 = [animationKey, controls];
    $[0] = animationKey;
    $[1] = controls;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  const outlineOpacity = isFavorited ? 0 : 1;
  const solidOpacity = isFavorited ? 1 : 0;
  let t3;
  if ($[4] !== animationKey) {
    t3 = animationKey > 0 ? {
      duration: 0.15
    } : {
      duration: 0
    };
    $[4] = animationKey;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const opacityTransition = t3;
  let t4;
  if ($[6] !== className) {
    t4 = cn("relative inline-flex", className);
    $[6] = className;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      scale: 1
    };
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  let t7;
  if ($[9] !== outlineOpacity) {
    t6 = {
      opacity: outlineOpacity
    };
    t7 = {
      opacity: outlineOpacity
    };
    $[9] = outlineOpacity;
    $[10] = t6;
    $[11] = t7;
  } else {
    t6 = $[10];
    t7 = $[11];
  }
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <HeartIconOutline className="h-full w-full" />;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== isFavorited || $[14] !== opacityTransition || $[15] !== t6 || $[16] !== t7) {
    t9 = <motion.span className="absolute inset-0 inline-flex" initial={t6} animate={t7} transition={opacityTransition} aria-hidden={isFavorited}>{t8}</motion.span>;
    $[13] = isFavorited;
    $[14] = opacityTransition;
    $[15] = t6;
    $[16] = t7;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  let t11;
  if ($[18] !== solidOpacity) {
    t10 = {
      opacity: solidOpacity
    };
    t11 = {
      opacity: solidOpacity
    };
    $[18] = solidOpacity;
    $[19] = t10;
    $[20] = t11;
  } else {
    t10 = $[19];
    t11 = $[20];
  }
  const t12 = !isFavorited;
  let t13;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <HeartIconSolid className="h-full w-full" />;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== opacityTransition || $[23] !== t10 || $[24] !== t11 || $[25] !== t12) {
    t14 = <motion.span className="absolute inset-0 inline-flex" initial={t10} animate={t11} transition={opacityTransition} aria-hidden={t12}>{t13}</motion.span>;
    $[22] = opacityTransition;
    $[23] = t10;
    $[24] = t11;
    $[25] = t12;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== controls || $[28] !== t14 || $[29] !== t4 || $[30] !== t9) {
    t15 = <motion.span className={t4} animate={controls} initial={t5}>{t9}{t14}</motion.span>;
    $[27] = controls;
    $[28] = t14;
    $[29] = t4;
    $[30] = t9;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  return t15;
}
export const FavoriteButton = props => {
  const $ = _c(29);
  const {
    size: t0,
    tone: t1,
    removeOutline: t2,
    className,
    isFavorited: t3,
    entityId
  } = props;
  const size = t0 === undefined ? "small" : t0;
  const tone = t1 === undefined ? "dark" : t1;
  const removeOutline = t2 === undefined ? false : t2;
  const snapshotFallback = t3 === undefined ? false : t3;
  const [animationKey, setAnimationKey] = useState(0);
  let t4;
  if ($[0] !== entityId || $[1] !== snapshotFallback) {
    t4 = {
      entityId,
      snapshotFallback
    };
    $[0] = entityId;
    $[1] = snapshotFallback;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const {
    isFavorited,
    updateIsFavorited
  } = useFavoriteState(t4);
  let t5;
  if ($[3] !== isFavorited || $[4] !== props || $[5] !== updateIsFavorited) {
    t5 = async ev => {
      const wasFavorited = isFavorited;
      const nextFavorited = !wasFavorited;
      ev.stopPropagation();
      setAnimationKey(_temp);
      updateIsFavorited(nextFavorited);
      props.onFavoriteClick?.(nextFavorited);
      ;
      try {
        if (wasFavorited) {
          await instrumentAction("UserActions.unfavoriteEntity", unfavoriteEntity)({
            entityId: props.entityId,
            entityType: props.entityType
          });
        } else {
          await instrumentAction("UserActions.favoriteEntity", favoriteEntity)({
            entityId: props.entityId,
            entityType: props.entityType
          });
        }
      } catch (t6) {
        const error = t6;
        log.error("Error updating favorite", {
          error: error.message
        });
        updateIsFavorited(wasFavorited);
      }
    };
    $[3] = isFavorited;
    $[4] = props;
    $[5] = updateIsFavorited;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const onUpdateFavorite = t5;
  if (!props.entityId || !props.entityType) {
    return null;
  }
  if (size === "large") {
    let t6;
    if ($[7] !== className || $[8] !== removeOutline) {
      t6 = cn("flex h-10 w-10 cursor-pointer items-center justify-center rounded-full md:h-15 md:w-15", !removeOutline && ["border border-white/50", "transition-[background,color,border-color] duration-200", "hover:bg-white/10 focus:bg-white/10 active:bg-white/20"], "bg-transparent text-bodi-white", className);
      $[7] = className;
      $[8] = removeOutline;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    let t7;
    if ($[10] !== animationKey || $[11] !== isFavorited) {
      t7 = <AnimatedFavoriteIcon isFavorited={isFavorited} animationKey={animationKey} className="h-6 w-6 md:h-7.5 md:w-7.5" />;
      $[10] = animationKey;
      $[11] = isFavorited;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    let t8;
    if ($[13] !== isFavorited || $[14] !== onUpdateFavorite || $[15] !== t6 || $[16] !== t7) {
      t8 = <button type="button" onClick={onUpdateFavorite} data-favorited={isFavorited} className={t6}>{t7}</button>;
      $[13] = isFavorited;
      $[14] = onUpdateFavorite;
      $[15] = t6;
      $[16] = t7;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    return t8;
  }
  const iconClass = tone === "light" ? "h-6 w-6 text-bodi-black-text" : "h-6 w-6 text-bodi-white";
  const t6 = !removeOutline && "border border-bodi-gray-medium";
  let t7;
  if ($[18] !== className || $[19] !== t6) {
    t7 = cn("flex h-10 w-10 items-center justify-center rounded-full", t6, className);
    $[18] = className;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== animationKey || $[22] !== iconClass || $[23] !== isFavorited) {
    t8 = <AnimatedFavoriteIcon isFavorited={isFavorited} animationKey={animationKey} className={iconClass} />;
    $[21] = animationKey;
    $[22] = iconClass;
    $[23] = isFavorited;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== onUpdateFavorite || $[26] !== t7 || $[27] !== t8) {
    t9 = <button type="button" className={t7} onClick={onUpdateFavorite} data-testid="tile_button_favorite">{t8}</button>;
    $[25] = onUpdateFavorite;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  return t9;
};
function _temp(k) {
  return k + 1;
}